import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Badge, Row, Col } from 'react-bootstrap';
import { FaFileSignature, FaPencilAlt, FaTimes, FaLock } from 'react-icons/fa';
import axiosInstance from '../../utils/axios';
import { getRole } from '../../utils/role';

export default function PartnerDisclaimer({ targetAdminId }) {
  const role = getRole();
  const isSupport = role === 'SUPPORT';
  const isDisclaimerLocked = !isSupport;

  const [disclaimers, setDisclaimers] = useState({ soil: '', water: '' });
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const fetchDisclaimers = async () => {
      setLoading(true);
      try {
        const endpoint = `/partner/disclaimer/${targetAdminId}`;
        const res = await axiosInstance.get(endpoint);
        if (res.data.success) {
          setDisclaimers({
            soil: res.data.custom_disclaimer_soil || '',
            water: res.data.custom_disclaimer_water || ''
          });
        }
      } catch (err) {
        console.error("Failed to fetch disclaimers", err);
      } finally {
        setLoading(false);
      }
    };

    if (targetAdminId) fetchDisclaimers();
  }, [targetAdminId]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setMessage({ type: '', text: '' });

    try {
      const endpoint = `/partner/disclaimer/${targetAdminId}`;
      const res = await axiosInstance.put(endpoint, {
        custom_disclaimer_soil: disclaimers.soil,
        custom_disclaimer_water: disclaimers.water
      });

      if (res.data.success) {
        setMessage({ type: 'success', text: 'Disclaimers updated successfully! 🎉' });
        setDisclaimers({
          soil: res.data.data.custom_disclaimer_soil || '',
          water: res.data.data.custom_disclaimer_water || ''
        });
        setTimeout(() => {
          setEditMode(false);
          setMessage({ type: '', text: '' });
        }, 1500);
      }
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.msg || 'Failed to update.' });
    } finally {
      setUpdating(false);
    }
  };

  const handleChange = (e) => {
    setDisclaimers(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (loading) {
    return (
      <Card className="rounded-4 shadow-sm border-0 mt-4">
        <Card.Body className="text-center p-4">
          <Spinner animation="border" variant="secondary" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="rounded-4 shadow-sm border-0 mt-4">
      <Card.Body className="p-0">
        <div className="d-flex justify-content-between align-items-center p-4 border-bottom">
          <h5 className="fw-bold mb-0 d-flex align-items-center gap-2 text-[#134225]">
            <FaFileSignature /> Custom Report Disclaimers
            {isDisclaimerLocked && <Badge bg="warning" text="dark" className="ms-2"><FaLock size={10} className="me-1" /> Locked</Badge>}
          </h5>
          {!isDisclaimerLocked && (
            <div className="cursor-pointer text-secondary">
              {editMode ? (
                <FaTimes size={18} onClick={() => { setEditMode(false); setMessage({ type: '', text: '' }); }} />
              ) : (
                <FaPencilAlt size={18} onClick={() => setEditMode(true)} />
              )}
            </div>
          )}
        </div>

        <div className="p-4 bg-light rounded-bottom-4">
          {message.text && <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}

          {editMode ? (
            <Form onSubmit={handleUpdate}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-secondary fw-bold">Soil Report Disclaimer</Form.Label>
                    <Form.Control as="textarea" rows={5} name="soil" value={disclaimers.soil} onChange={handleChange} placeholder="Leave blank for default..." disabled={isDisclaimerLocked} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-secondary fw-bold">Water Report Disclaimer</Form.Label>
                    <Form.Control as="textarea" rows={5} name="water" value={disclaimers.water} onChange={handleChange} placeholder="Leave blank for default..." disabled={isDisclaimerLocked} />
                  </Form.Group>
                </Col>
              </Row>
              <Button type="submit" variant="primary" disabled={updating || isDisclaimerLocked}>
                {updating ? <><Spinner size="sm" className="me-2" />Saving...</> : 'Save Disclaimers'}
              </Button>
            </Form>
          ) : (
            <Row>
              <Col md={6}>
                <h6 className="text-secondary fw-bold border-bottom pb-2">Soil Report</h6>
                <div className="text-gray-700 bg-white p-3 rounded border h-100">
                  {disclaimers.soil ? <p className="mb-0 text-justify" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{disclaimers.soil}</p> : <p className="mb-0 text-muted fst-italic">Default template active.</p>}
                </div>
              </Col>
              <Col md={6}>
                <h6 className="text-secondary fw-bold border-bottom pb-2">Water Report</h6>
                <div className="text-gray-700 bg-white p-3 rounded border h-100">
                  {disclaimers.water ? <p className="mb-0 text-justify" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{disclaimers.water}</p> : <p className="mb-0 text-muted fst-italic">Default template active.</p>}
                </div>
              </Col>
            </Row>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}